import { ApolloClient } from 'apollo-client'
import { split, ApolloLink, Observable } from 'apollo-link'
import { setContext } from 'apollo-link-context'
import { HttpLink } from 'apollo-link-http'
import { getMainDefinition } from 'apollo-utilities'
import { InMemoryCache } from 'apollo-cache-inmemory'
import fetch from 'node-fetch'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { RetryLink } from '@apollo/client/link/retry'
import { onError } from 'apollo-link-error'
import { logEvent } from './common/diagnostics'

// Centrally capture GraphQL + network errors. Our errorPolicy is 'ignore'/'all'
// so these otherwise never throw — the store just proceeds with null/partial
// data. During the Deltafusor migration this is the main source of silent
// "nothing works" reports, so surface every one to the diagnostics buffer.
// Single shared refresh so a burst of concurrent 401/403s triggers only one
// rehydrate/exchange. Resolves true if the Deltafusor token was recovered.
// Resolves false without retrying when recovery is impossible: a null token
// means api.public denied the exchange for no active subscription (the store
// has already routed to the expired screen); a rejection means the Cognito
// session is gone (user has been routed to login).
let refreshInFlight = null
function recoverDeltafusorToken () {
  if (!refreshInFlight) {
    refreshInFlight = window.doApp.$store
      .dispatch('account/refreshDeltafusorToken', null, { root: true })
      .then((token) => !!token)
      .catch(() => {
        window.doApp.$store.dispatch('account/sessionExpired', null, { root: true })
        return false
      })
      .then(result => { refreshInFlight = null; return result })
  }
  return refreshInFlight
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const query = operation.query && operation.query.loc && operation.query.loc.source.body.slice(0, 120)
  if (graphQLErrors) {
    graphQLErrors.forEach(err => logEvent('error', 'graphql-error', {
      operation: operation.operationName,
      message: err.message,
      path: err.path,
      query
    }))
  }
  if (networkError) {
    logEvent('error', 'network-error', {
      operation: operation.operationName,
      message: networkError.message,
      status: networkError.statusCode,
      query
    })
  }

  // Deltafusor rejected our token (403 "Invalid token", or 401). Usually the
  // Cognito session lapsed and state.user went null, so the token stopped
  // refreshing. Recover once — rehydrate the session + re-exchange — then retry
  // the operation with the fresh token. The context flag prevents a retry loop.
  const status = networkError && networkError.statusCode
  if (networkError && (status === 401 || status === 403) && !operation.getContext().__authRetried) {
    operation.setContext({ __authRetried: true })
    return new Observable(observer => {
      recoverDeltafusorToken()
        .then(recovered => {
          logEvent(recovered ? 'info' : 'error', 'auth-recovery', {
            status,
            recovered,
            operation: operation.operationName
          })
          if (!recovered) {
            // Session gone; sessionExpired already routed to login. Complete
            // quietly so we don't retry straight into another 403.
            observer.complete()
            return
          }
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          })
        })
        .catch(err => observer.error(err))
    })
  }
})

const withToken = setContext(async () => {
  await window.doApp.$store.dispatch('account/updateCoreAPIToken', null, { root: true })
  const token = window.localStorage.getItem('coreapi_auth_token')
  const headers = {
    // Make sure to actually set the headers here
    authorization: `Bearer ${token}` || null
  }
  // Lets us find a user's requests (incl. ones Cloudflare blocked) in Cloudflare
  // logs by username. Only enable for endpoints behind api.graphql.proxy, whose
  // CORS config allows this header — elsewhere the preflight would fail.
  const username = window.doApp.$store.getters['account/getUsername']
  if (process.env.VUE_APP_SEND_DO_USER_HEADER === 'true' && username) {
    // fetch throws on non-Latin1 header values, which would fail the request.
    headers['x-do-user'] = /^[\x20-\x7E]+$/.test(username) ? username : encodeURIComponent(username)
  }
  return { headers }
})

const httpLink = ApolloLink.from([withToken, new HttpLink({
  uri: `${process.env.VUE_APP_GRAPHQL_HTTP}query`,
  options: { mode: 'no-cors' }
})])

// Captured on each connect so the visibilitychange handler can force-close
// a likely-dead socket when the tab returns from background suspension.
let activeWsSocket = null

const subscriptionLink = process.browser ? new GraphQLWsLink(createClient({
  url: `${process.env.VUE_APP_GRAPHQL_WS}`,
  lazy: true,
  retryAttempts: Infinity,
  shouldRetry: () => true,
  connectionParams: async () => {
    await window.doApp.$store.dispatch('account/updateCoreAPIToken', null, { root: true })
    const token = window.localStorage.getItem('coreapi_auth_token')
    return {
      Authorization: `Bearer ${token}`
    }
  },
  on: {
    connected: (socket) => { activeWsSocket = socket },
    closed: () => { activeWsSocket = null }
  }
})) : null

if (process.browser) {
  // When a backgrounded tab is suspended, Chrome freezes the JS runtime so we
  // stop responding to server pings. The server closes the connection but we
  // don't notice until the tab returns. Force-close the (likely-dead) socket
  // so graphql-ws reopens with a fresh token and resubscribes everything.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    if (!activeWsSocket) return
    if (activeWsSocket.readyState === WebSocket.OPEN) {
      activeWsSocket.close(4205, 'tab visible — force reconnect')
    }
  })
}

const subLink = process.browser ? subscriptionLink : null

const splitLink = process.browser ? split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  subLink,
  httpLink
) : httpLink

const retryLink = new RetryLink({
  delay: {
    initial: 2 * 1000,
    max: 64 * 1000,
    jitter: true
  },
  attempts: {
    max: 3,
    // Do not retry on 4xx responses — retrying a 429 (rate limit) or 401
    // (auth) just makes both problems worse.
    retryIf: (error) => {
      const status = error?.statusCode || error?.networkError?.statusCode
      if (status && status >= 400 && status < 500) return false
      return !!error
    }
  }
})

const link = ApolloLink.from([
  errorLink,
  retryLink,
  splitLink
])

const cache = new InMemoryCache({ resultCaching: false })

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore'
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  }
}

export const subscriptionClient = subscriptionLink?.subscriptionClient || {}

export const apolloClient = new ApolloClient({
  link,
  fetch: fetch,
  cache: cache,
  defaultOptions
})
